import React, { useState, useMemo } from 'react';
import {
  Bookmark, Search, Tag, Trash2, Download, ChevronDown, ChevronUp,
  AlertTriangle, Edit3, LayoutTemplate, Package, Plus, ShoppingCart
} from 'lucide-react';
import { useTheme } from '../../core/ui/ThemeContext';
import { useLanguage } from '../../core/i18n/LanguageContext';

/**
 * Panel mit der Liste gespeicherter Warenkörbe.
 * Suche (Name/Tags/Notiz) + Tag-Filter-Chips + pro Karte: Name, Tag-Chips,
 * Positionsanzahl, Summe, Vorlage-Badge, Aktionen Laden(Ersetzen)/Anhängen/Umbenennen/Löschen.
 * OCI-Artikel-Badge „Preis prüfen" wenn shop_id gesetzt.
 */
const SavedCartsPanel = ({
  savedCarts = [],
  onLoad,        // (savedCart, mode: 'replace'|'append') => void
  onRename,      // (id, fields) => void  (öffnet Umbenennen-Dialog)
  onDelete,      // (id) => void
  collapsible = true,
  defaultOpen = false,
}) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();

  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTag, setActiveTag] = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editNote, setEditNote] = useState('');
  const [editTags, setEditTags] = useState('');

  // Alle vorhandenen Tags aus den gespeicherten Warenkörben extrahieren
  const allTags = useMemo(() => {
    const tagSet = new Set();
    if (Array.isArray(savedCarts)) {
      savedCarts.forEach((sc) => {
        if (sc.tags) {
          sc.tags.split(',').map((t) => t.trim()).filter(Boolean).forEach((tag) => tagSet.add(tag));
        }
      });
    }
    return Array.from(tagSet).sort();
  }, [savedCarts]);

  // Filter anwenden: Suchbegriff + Tag
  const filtered = useMemo(() => {
    if (!Array.isArray(savedCarts)) return [];
    return savedCarts.filter((sc) => {
      if (activeTag) {
        const tags = sc.tags ? sc.tags.split(',').map((t) => t.trim()) : [];
        if (!tags.includes(activeTag)) return false;
      }
      if (searchTerm.trim()) {
        const q = searchTerm.trim().toLowerCase();
        const haystack = [sc.name, sc.note, sc.tags].filter(Boolean).join(' ').toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [savedCarts, searchTerm, activeTag]);

  const formatEur = (val) =>
    new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 }).format(parseFloat(val) || 0);

  const getItemCount = (sc) => {
    if (Array.isArray(sc.items)) return sc.items.length;
    return 0;
  };

  const hasOciItems = (sc) => {
    if (!Array.isArray(sc.items)) return false;
    return sc.items.some((item) => item.shop_id != null && item.shop_id !== '');
  };

  const getTagList = (sc) => {
    if (!sc.tags) return [];
    return sc.tags.split(',').map((t) => t.trim()).filter(Boolean);
  };

  const startEdit = (sc) => {
    setEditingId(sc.id);
    setEditName(sc.name || '');
    setEditNote(sc.note || '');
    setEditTags(sc.tags || '');
  };

  const cancelEdit = () => setEditingId(null);

  const confirmEdit = (id) => {
    if (!editName.trim()) return;
    onRename(id, { name: editName.trim(), note: editNote.trim(), tags: editTags.trim() });
    setEditingId(null);
  };

  const handleDelete = (id) => {
    setConfirmDeleteId(null);
    onDelete(id);
  };

  // Styles
  const cardBase = `rounded-2xl border transition-all duration-200 ${
    isDarkMode
      ? 'bg-gray-900/40 border-white/10 hover:border-white/20'
      : 'bg-white border-slate-200 hover:border-brand/30'
  }`;

  const inputCls = `w-full rounded-xl px-3 py-2 text-sm border focus:ring-2 focus:ring-brand/40 focus:outline-none transition-colors ${
    isDarkMode
      ? 'bg-gray-800/40 text-white border-white/10 placeholder-white/30'
      : 'bg-white text-slate-800 border-slate-200 placeholder-slate-300'
  }`;

  const searchInputCls = `w-full pl-8 pr-3 py-2 rounded-xl text-sm border focus:ring-2 focus:ring-brand/40 focus:outline-none transition-colors ${
    isDarkMode
      ? 'bg-gray-800/40 text-white border-white/10 placeholder-white/40'
      : 'bg-white text-slate-800 border-slate-200 placeholder-slate-300'
  }`;

  const panelHeader = (
    <button
      onClick={() => collapsible && setIsOpen((v) => !v)}
      className={`w-full flex items-center justify-between px-4 py-3 rounded-2xl transition-colors ${
        isDarkMode
          ? 'bg-gray-900/40 border border-white/10 hover:border-white/20'
          : 'bg-white border border-slate-200 hover:border-brand/30'
      } ${collapsible ? 'cursor-pointer' : 'cursor-default'}`}
    >
      <div className="flex items-center gap-2.5">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isDarkMode ? 'bg-brand/20' : 'bg-brand/10'}`}>
          <Bookmark className={`w-4 h-4 ${isDarkMode ? 'text-blue-300' : 'text-brand'}`} />
        </div>
        <span className={`font-semibold text-sm ${isDarkMode ? 'text-white/90' : 'text-slate-800'}`}>
          {t('savedCarts.panel.title')}
        </span>
        {Array.isArray(savedCarts) && savedCarts.length > 0 && (
          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
            isDarkMode ? 'bg-brand/20 text-blue-200 border border-brand/30' : 'bg-brand/10 text-brand border border-brand/20'
          }`}>
            {savedCarts.length}
          </span>
        )}
      </div>
      {collapsible && (
        isOpen
          ? <ChevronUp className={`w-4 h-4 ${isDarkMode ? 'text-white/50' : 'text-slate-400'}`} />
          : <ChevronDown className={`w-4 h-4 ${isDarkMode ? 'text-white/50' : 'text-slate-400'}`} />
      )}
    </button>
  );

  const panelBody = (
    <div className="space-y-3 mt-3">
      {/* Suche + Tag-Filter */}
      <div className="space-y-2">
        <div className="relative">
          <Search className={`absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 ${isDarkMode ? 'text-white/30' : 'text-slate-400'}`} />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={t('savedCarts.panel.searchPlaceholder')}
            className={searchInputCls}
          />
        </div>
        {allTags.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            <button
              onClick={() => setActiveTag('')}
              className={`px-2.5 py-1 rounded-full text-xs font-medium transition-all ${
                activeTag === ''
                  ? 'bg-brand text-white'
                  : isDarkMode
                    ? 'bg-white/10 text-white/60 hover:bg-white/15'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {t('common.all')}
            </button>
            {allTags.map((tag) => (
              <button
                key={tag}
                onClick={() => setActiveTag(tag === activeTag ? '' : tag)}
                className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition-all ${
                  activeTag === tag
                    ? 'bg-brand text-white'
                    : isDarkMode
                      ? 'bg-white/10 text-white/60 hover:bg-white/15'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                <Tag className="w-2.5 h-2.5" />
                {tag}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Leerer Zustand */}
      {filtered.length === 0 && (
        <div className={`flex flex-col items-center py-8 rounded-2xl border ${
          isDarkMode ? 'border-white/10 bg-gray-900/20' : 'border-slate-200 bg-slate-50'
        }`}>
          <Bookmark className={`w-8 h-8 mb-2 ${isDarkMode ? 'text-white/20' : 'text-slate-300'}`} />
          <p className={`text-sm font-medium ${isDarkMode ? 'text-white/50' : 'text-slate-500'}`}>
            {Array.isArray(savedCarts) && savedCarts.length === 0
              ? t('savedCarts.panel.empty')
              : t('savedCarts.panel.emptyFiltered')}
          </p>
          {(searchTerm || activeTag) && (
            <button
              onClick={() => { setSearchTerm(''); setActiveTag(''); }}
              className={`mt-2 text-xs font-medium ${isDarkMode ? 'text-brand/80 hover:text-brand' : 'text-brand hover:text-[#1D4ED8]'} transition-colors`}
            >
              {t('savedCarts.panel.clearFilter')}
            </button>
          )}
        </div>
      )}

      {/* Karten-Liste */}
      <div className="space-y-2">
        {filtered.map((sc) => (
          <div key={sc.id} className={cardBase}>
            {editingId === sc.id ? (
              /* Bearbeitungsmodus */
              <div className="p-4 space-y-3">
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  placeholder={t('savedCarts.modal.nameLabel')}
                  className={inputCls}
                  autoFocus
                />
                <input
                  type="text"
                  value={editTags}
                  onChange={(e) => setEditTags(e.target.value)}
                  placeholder={t('savedCarts.modal.tagsPlaceholder')}
                  className={inputCls}
                />
                <textarea
                  value={editNote}
                  onChange={(e) => setEditNote(e.target.value)}
                  placeholder={t('savedCarts.modal.notePlaceholder')}
                  rows={2}
                  className={`${inputCls} resize-none`}
                />
                <div className="flex items-center justify-end gap-2">
                  <button
                    onClick={cancelEdit}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                      isDarkMode ? 'text-white/60 hover:bg-white/10' : 'text-slate-500 hover:bg-slate-100'
                    }`}
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    onClick={() => confirmEdit(sc.id)}
                    disabled={!editName.trim()}
                    className="px-3 py-1.5 rounded-lg text-xs font-medium text-white bg-brand hover:bg-[#1D4ED8] disabled:opacity-50 transition-colors"
                  >
                    {t('common.save')}
                  </button>
                </div>
              </div>
            ) : confirmDeleteId === sc.id ? (
              /* Lösch-Bestätigung */
              <div className={`p-4 rounded-2xl ${isDarkMode ? 'bg-red-900/20 border border-red-500/30' : 'bg-red-50 border border-red-200'}`}>
                <p className={`text-sm font-medium mb-3 ${isDarkMode ? 'text-red-200' : 'text-red-800'}`}>
                  {t('savedCarts.panel.deleteConfirm', { name: sc.name })}
                </p>
                <div className="flex items-center justify-end gap-2">
                  <button
                    onClick={() => setConfirmDeleteId(null)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                      isDarkMode ? 'text-white/60 hover:bg-white/10' : 'text-slate-600 hover:bg-slate-100'
                    }`}
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    onClick={() => handleDelete(sc.id)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-white bg-red-600 hover:bg-red-700 transition-colors"
                  >
                    <Trash2 className="w-3 h-3" />
                    {t('common.delete')}
                  </button>
                </div>
              </div>
            ) : (
              /* Normale Karte */
              <div className="p-4">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-sm font-semibold truncate ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                        {sc.name}
                      </span>
                      {sc.is_template === 1 && (
                        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold flex-shrink-0 ${
                          isDarkMode ? 'bg-brand/20 text-blue-200 border border-brand/30' : 'bg-brand/10 text-brand border border-brand/20'
                        }`}>
                          <LayoutTemplate className="w-2.5 h-2.5" />
                          {t('savedCarts.panel.templateBadge')}
                        </span>
                      )}
                      {hasOciItems(sc) && (
                        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold flex-shrink-0 ${
                          isDarkMode ? 'bg-amber-500/20 text-amber-200 border border-amber-400/30' : 'bg-amber-50 text-amber-700 border border-amber-200'
                        }`}>
                          <AlertTriangle className="w-2.5 h-2.5" />
                          {t('savedCarts.panel.priceCheckBadge')}
                        </span>
                      )}
                    </div>
                    {sc.note && (
                      <p className={`text-xs mt-0.5 truncate ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>
                        {sc.note}
                      </p>
                    )}
                  </div>
                </div>

                {/* Tags */}
                {getTagList(sc).length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-2">
                    {getTagList(sc).map((tag) => (
                      <span
                        key={tag}
                        className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium ${
                          isDarkMode ? 'bg-white/10 text-white/50' : 'bg-slate-100 text-slate-500'
                        }`}
                      >
                        <Tag className="w-2 h-2" />
                        {tag}
                      </span>
                    ))}
                  </div>
                )}

                {/* Meta-Zeile */}
                <div className={`flex items-center gap-3 text-xs mb-3 ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>
                  <span className="flex items-center gap-1">
                    <Package className="w-3 h-3" />
                    {t('savedCarts.panel.itemCount', { count: getItemCount(sc) })}
                  </span>
                  <span>·</span>
                  <span className={`font-medium ${isDarkMode ? 'text-white/70' : 'text-slate-700'}`}>
                    {formatEur(sc.total_amount)}
                  </span>
                  {sc.currency && sc.currency !== 'EUR' && (
                    <>
                      <span>·</span>
                      <span>{sc.currency}</span>
                    </>
                  )}
                </div>

                {/* Aktions-Zeile */}
                <div className="flex items-center gap-1.5 flex-wrap">
                  {/* Vorlage: 1-Klick-Laden (Ersetzen) mit prominentem Button */}
                  {sc.is_template === 1 ? (
                    <button
                      onClick={() => onLoad(sc, 'replace')}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-white bg-brand hover:bg-[#1D4ED8] transition-colors"
                    >
                      <ShoppingCart className="w-3 h-3" />
                      {t('savedCarts.panel.loadReplace')}
                    </button>
                  ) : (
                    <button
                      onClick={() => onLoad(sc, 'replace')}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                        isDarkMode
                          ? 'bg-brand/20 hover:bg-brand/30 text-blue-200 border border-brand/30'
                          : 'bg-brand/10 hover:bg-brand/20 text-brand border border-brand/20'
                      }`}
                    >
                      <Download className="w-3 h-3" />
                      {t('savedCarts.panel.loadReplace')}
                    </button>
                  )}

                  <button
                    onClick={() => onLoad(sc, 'append')}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                      isDarkMode
                        ? 'bg-white/8 hover:bg-white/12 text-white/70 border border-white/10'
                        : 'bg-slate-50 hover:bg-slate-100 text-slate-600 border border-slate-200'
                    }`}
                  >
                    <Plus className="w-3 h-3" />
                    {t('savedCarts.panel.loadAppend')}
                  </button>

                  <button
                    onClick={() => startEdit(sc)}
                    className={`flex items-center gap-1 p-1.5 rounded-lg transition-colors ml-auto ${
                      isDarkMode ? 'text-white/40 hover:text-white/70 hover:bg-white/8' : 'text-slate-400 hover:text-slate-700 hover:bg-slate-100'
                    }`}
                    title={t('savedCarts.panel.renameBtn')}
                  >
                    <Edit3 className="w-3.5 h-3.5" />
                  </button>

                  <button
                    onClick={() => setConfirmDeleteId(sc.id)}
                    className={`flex items-center gap-1 p-1.5 rounded-lg transition-colors ${
                      isDarkMode ? 'text-red-400/60 hover:text-red-300 hover:bg-red-900/20' : 'text-red-400 hover:text-red-600 hover:bg-red-50'
                    }`}
                    title={t('common.delete')}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  if (collapsible) {
    return (
      <div>
        {panelHeader}
        {isOpen && panelBody}
      </div>
    );
  }

  return (
    <div>
      {panelHeader}
      {panelBody}
    </div>
  );
};

export default SavedCartsPanel;
